import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Import themes
import originalTheme from '../theme/designSystem';
import pulseTheme from '../theme/pulseTheme';

// Create context
const ThemeContext = createContext();

// Theme options
export const THEME_OPTIONS = {
    ORIGINAL: 'original',
    PULSE: 'pulse',
};

// Theme mapping
const themes = {
    [THEME_OPTIONS.ORIGINAL]: originalTheme,
    [THEME_OPTIONS.PULSE]: pulseTheme,
};

// Theme names for display
export const themeNames = {
    [THEME_OPTIONS.ORIGINAL]: 'Original Theme',
    [THEME_OPTIONS.PULSE]: 'synaptix Pulse',
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // Get theme from localStorage or default to original
    const [currentTheme, setCurrentTheme] = useState(() => {
        const savedTheme = localStorage.getItem('app-theme');
        return savedTheme && themes[savedTheme] ? savedTheme : THEME_OPTIONS.ORIGINAL;
    });

    // Update localStorage when theme changes
    useEffect(() => {
        localStorage.setItem('app-theme', currentTheme);
    }, [currentTheme]);

    // Toggle between themes
    const toggleTheme = () => {
        setCurrentTheme(prev => 
            prev === THEME_OPTIONS.ORIGINAL 
                ? THEME_OPTIONS.PULSE 
                : THEME_OPTIONS.ORIGINAL
        );
    };

    // Set specific theme
    const setTheme = (themeName) => {
        if (themes[themeName]) {
            setCurrentTheme(themeName);
        }
    };

    // Get current theme object
    const theme = themes[currentTheme];

    const value = {
        currentTheme,
        theme,
        toggleTheme,
        setTheme,
        themeNames,
        THEME_OPTIONS,
    };

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
